import DialogTitle from './ui/FitDialogTitle';
import SingleLineText from './ui/SingleLineText';
import Button from './ui/FitButton';
import Dialog from './StableDialog';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Card, CardContent, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import { adminOperations } from '../api';
import LoadingLabel from './LoadingLabel';

const PHASE = { STARTING: '준비', FETCH: '코드 받기', BUILD: '빌드', START_SERVER: '새 서버 시작', HEALTH: '정상 동작 확인', SWITCH: '서버 전환', CLEANUP: '이전 서버 정리' };
const STATE = { RUNNING: '배포 중', SUCCEEDED: '배포 완료', FAILED_OR_INTERRUPTED: '실패 또는 중단 — 서버 로그를 확인해주세요' };
const HISTORY_PREVIEW_COUNT = 3;
const kstFormatter = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
});

function formatKstTime(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return `${kstFormatter.format(date)} KST`;
}

export default function AdminOperations() {
    const [key, setKey] = useState('');
    const [unlocked, setUnlocked] = useState(false);
    const [status, setStatus] = useState(null);
    const [sql, setSql] = useState('SHOW TABLES;');
    const [backup, setBackup] = useState('');
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [connectionError, setConnectionError] = useState('');
    const [result, setResult] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const lock = useRef(false);
    const history = status?.history || [];
    const visibleHistory = showAllHistory ? history : history.slice(0, HISTORY_PREVIEW_COUNT);
    const hiddenHistoryCount = Math.max(0, history.length - HISTORY_PREVIEW_COUNT);

    useEffect(() => {
        if (!unlocked) return undefined;
        let active = true;
        let timer;
        const poll = async () => {
            try {
                const next = await adminOperations(key);
                if (active) { setStatus(next); setConnectionError(''); }
            } catch (_) {
                if (active) setConnectionError('상태를 확인하지 못했어요. 서버 전환 중이면 잠시 후 다시 연결됩니다.');
            }
            if (active) timer = setTimeout(poll, 5000);
        };
        timer = setTimeout(poll, 5000);
        return () => { active = false; clearTimeout(timer); };
    }, [unlocked, key]);

    const run = async (action, body) => {
        if (lock.current) return;
        lock.current = true;
        setBusy(action || 'status'); setError(''); setConnectionError(''); setResult(null);
        try {
            const data = await adminOperations(key, action, body);
            if (!action) { setStatus(data); setUnlocked(true); }
            else { setResult(data); if (data.backup) setBackup(data.backup); }
        } catch (e) {
            setError(e.response?.data?.message || '요청 결과를 확인하지 못했어요. 실행 이력을 확인한 뒤 재시도해주세요.');
        } finally { lock.current = false; setBusy(''); }
    };

    return <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #eee' }}><CardContent>
        <SingleLineText sx={{ fontWeight: 700, mb: 1 }}>Pi 배포 · DB 작업</SingleLineText>
        <Typography sx={{ color: '#777', mb: 2 }}>GitHub main의 서버와 웹을 배포합니다. 앱 OTA는 별도입니다.</Typography>
        <TextField fullWidth type="password" label="운영 키" value={key} autoComplete="off" disabled={!!busy}
            onChange={e => { setKey(e.target.value); setUnlocked(false); setStatus(null); setShowAllHistory(false); }} />
        <Button sx={{ my: 1 }} disabled={!!busy || !key} onClick={() => run()}>{busy === 'status' ? <LoadingLabel>확인 중</LoadingLabel> : '연결 확인'}</Button>
        {connectionError && <Alert severity="warning" sx={{ my: 1 }}>{connectionError}</Alert>}
        {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
        {unlocked && <>
            <Typography sx={{ my: 1 }}>{STATE[status?.deployment?.state] || '배포 실행 이력이 없습니다'} {status?.deployment?.state === 'RUNNING' && PHASE[status?.deployment?.phase]}</Typography>
            {status?.deployment?.updatedAt && <Typography variant="caption">최근 기록: {formatKstTime(status.deployment.updatedAt)}</Typography>}
            <Box><Button variant="contained" disabled={!!busy || status?.deployment?.state === 'RUNNING'} onClick={() => setConfirm({ action: 'deploy' })}>
                {busy === 'deploy' ? <LoadingLabel>요청 중</LoadingLabel> : 'main 배포하기'}
            </Button></Box>
            <SingleLineText sx={{ fontWeight: 700, mt: 3, mb: 1 }}>SQL 실행</SingleLineText>
            <Typography sx={{ color: '#777', mb: 1 }}>조회는 최대 100행입니다. DDL은 테이블·컬럼·인덱스 추가만 지원합니다.</Typography>
            <TextField fullWidth multiline minRows={5} label="SQL 한 문장" value={sql} onChange={e => setSql(e.target.value)} disabled={!!busy} inputProps={{ maxLength: 16000, style: { fontFamily: 'monospace' } }} />
            <Typography variant="caption" component="div" sx={{ my: 1 }}>예: SELECT id FROM pr_devices LIMIT 10;<br />ALTER TABLE pr_devices ADD COLUMN IF NOT EXISTS example_flag BOOLEAN NOT NULL DEFAULT FALSE;<br />복잡한 조인·함수·데이터 수정은 지원하지 않습니다.</Typography>
            <Button sx={{ my: 1 }} disabled={!!busy || status?.deployment?.state === 'RUNNING'} onClick={() => run('backup')}>{busy === 'backup' ? <LoadingLabel>백업 중</LoadingLabel> : 'DB 백업 만들기'}</Button>
            <SingleLineText variant="caption" component="div" sx={{ mb: 1 }}>압축 백업은 마지막 1개만 보관합니다.</SingleLineText>
            <TextField fullWidth label="DDL 실행 전 백업 파일 경로" placeholder="/home/pi/kkomok-db-backups/admin-console/backup-....sql.gz" value={backup} onChange={e => setBackup(e.target.value)} disabled={!!busy} />
            <Button sx={{ mt: 1 }} disabled={!!busy || !sql.trim() || status?.deployment?.state === 'RUNNING'} onClick={() => setConfirm({ action: 'query', body: { id: crypto.randomUUID(), sql, backup } })}>
                {busy === 'query' ? <LoadingLabel>실행 중</LoadingLabel> : 'SQL 확인 후 실행'}
            </Button>
            {result && <Box component="pre" sx={{ overflow: 'auto', maxHeight: 400, fontSize: 12, bgcolor: '#f8f8f8', p: 1 }}>{JSON.stringify(result, null, 2)}</Box>}
            <SingleLineText sx={{ fontWeight: 700, mt: 2 }}>최근 SQL 이력</SingleLineText>
            {visibleHistory.map(item => <Box key={item.id} sx={{ my: 1, overflowWrap: 'anywhere' }}>
                <Typography variant="caption">{formatKstTime(item.time)} · {item.state}</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.sql}</Typography>
            </Box>)}
            {hiddenHistoryCount > 0 && <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowAllHistory(value => !value)}>
                {showAllHistory ? '접기' : `${hiddenHistoryCount}건 더보기`}
            </Button>}
        </>}
        <Dialog dismissDisabled={Boolean(busy)} open={!!confirm} onClose={() => setConfirm(null)} fullWidth maxWidth="sm">
            <DialogTitle>{confirm?.action === 'deploy' ? 'Pi에 main을 배포할까요?' : '운영 DB에서 실행할까요?'}</DialogTitle>
            <DialogContent><Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{confirm?.action === 'deploy' ? '필요한 DB 변경을 먼저 적용했는지 확인해주세요. 배포가 시작되면 창을 닫아도 계속 진행됩니다.' : confirm?.body?.sql}</Typography></DialogContent>
            <DialogActions><Button onClick={() => setConfirm(null)}>취소</Button><Button onClick={() => { const request = confirm; setConfirm(null); run(request.action, request.body); }}>실행</Button></DialogActions>
        </Dialog>
    </CardContent></Card>;
}
